/* ==========================================================================
   AUMI — product-page.js
   Página de produto: variantes, galeria + lightbox, quantidade, "Ver mais",
   copiar cupom, compartilhar, "Complete seu ritual", barra fixa de compra,
   simulador de frete por CEP e registro de "vistos recentemente".

   SIMULADOR DE FRETE — LIMITAÇÃO IMPORTANTE
   A Shopify não tem rota pública que calcule frete de UM produto isolado.
   As rotas /cart/prepare_shipping_rates.json e /cart/async_shipping_rates.json
   calculam o frete do CARRINHO ATUAL da visitante. Por isso:
     - com itens na sacola, as opções mostradas valem para a sacola inteira
       (a variante desta página só entra no cálculo se já estiver na sacola);
     - com a sacola vazia, mostramos "Adicione à sacola para calcular o frete
       exato" e um botão que adiciona a variante atual e calcula em seguida.
   Não criamos carrinho temporário e não removemos itens depois do cálculo.
   As tarifas de apps de frete (Melhor Envio) chegam por essas mesmas rotas,
   como qualquer tarifa calculada, com nome, prazo e preço.
   O estado (UF) é deduzido pela faixa do CEP, porque a Shopify exige
   shipping_address[province] para endereços do Brasil.
   ========================================================================== */
(() => {
  if (window.__aumiProductPage) return;
  window.__aumiProductPage = true;

  const RECENT_KEY = 'aumi:recently-viewed';
  const CEP_KEY = 'aumi:cep';

  const aumi = () => window.AUMI || {};
  const routes = () => aumi().routes || {};
  const cfg = () => aumi().settings || {};
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const money = (cents) => {
    if (typeof aumi().formatMoney === 'function') return aumi().formatMoney(cents);
    return (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };
  const toast = (msg) => {
    if (typeof aumi().toast === 'function') aumi().toast(msg);
  };
  const cartBase = () => (routes().cart || '/cart').replace(/\/$/, '');

  const store = {
    get(key, fallback) {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
      } catch (e) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* modo privado: ignora */
      }
    },
  };

  /* ---------- preço (espelha snippets/price.liquid) ---------- */
  function priceMarkup(variant, opts) {
    const s = cfg();
    const price = Number(variant.price) || 0;
    const compare = Number(variant.compare_at_price) || 0;
    const onSale = compare > price;
    const pixPct = Number(s.pixDiscount) || 0;
    const pix = Math.floor((price * (100 - pixPct)) / 100);
    const maxN = Number(s.installmentsMax) || 1;
    const minCents = Number(s.installmentMin) || 1;
    let n = Math.floor(price / minCents);
    if (n > maxN) n = maxN;
    if (n < 1) n = 1;
    const parcela = Math.floor(price / n);

    const classes = ['price', `price--${opts.size || 'md'}`];
    if (onSale) classes.push('price--on-sale');
    if (!variant.available) classes.push('price--sold-out');

    let html = `<div class="${classes.join(' ')}" data-price-wrapper><div class="price__row">`;
    if (onSale) {
      html += `<s class="price__compare" data-compare-price><span class="visually-hidden">De</span>${money(compare)}</s>`;
    }
    html += `<span class="price__current" data-current-price>${onSale ? '<span class="visually-hidden">Por</span>' : ''}${money(price)}</span>`;
    if (onSale && opts.showBadge !== 'false') {
      html += `<span class="price__badge" data-sale-badge>–${Math.round(((compare - price) * 100) / compare)}%</span>`;
    }
    html += '</div>';
    if (opts.showPix === 'true' && pixPct > 0) {
      html += `<p class="price__pix" data-pix-price><strong>${money(pix)}</strong> no Pix <span class="muted">(${pixPct}% off)</span></p>`;
    }
    if (opts.showInstallments === 'true' && n > 1) {
      html += `<p class="price__installments" data-installments>ou ${n}x de ${money(parcela)} sem juros</p>`;
    }
    return `${html}</div>`;
  }

  function rememberProduct(handle) {
    if (!handle) return;
    const list = store.get(RECENT_KEY, []).filter((h) => h !== handle);
    list.unshift(handle);
    store.set(RECENT_KEY, list.slice(0, 16));
  }

  /* ---------- product-info: variantes, quantidade, estados ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      try {
        this.variants = json ? JSON.parse(json.textContent) : [];
      } catch (e) {
        this.variants = [];
      }
      this.form = document.getElementById(this.dataset.formId);

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-position]')) this.onOptionChange();
      });
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-qty-step]');
        if (step) this.stepQuantity(step);
      });

      this.updateAvailability(this.selectedOptions());
      rememberProduct(this.dataset.productHandle);

      /* formulário "Avise-me" enviado: mostra a confirmação mesmo que a variante esteja disponível */
      this.querySelectorAll('[data-notify-success]').forEach((el) => {
        const wrap = el.closest('[data-notify]');
        if (wrap) wrap.hidden = false;
      });
    }

    stepQuantity(button) {
      const input = button.closest('[data-product-qty]')?.querySelector('input');
      if (!input) return;
      const min = Number(input.min) || 1;
      const next = Math.max(min, (Number(input.value) || min) + Number(button.dataset.qtyStep));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('[data-option-index]'), (fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    updateAvailability(selected) {
      this.querySelectorAll('[data-option-index]').forEach((fieldset) => {
        const index = Number(fieldset.dataset.optionIndex);
        fieldset.querySelectorAll('input[data-option-position]').forEach((input) => {
          const available = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options[index] === input.value &&
              variant.options.slice(0, index).every((value, i) => value === selected[i])
          );
          const label = input.nextElementSibling;
          if (!label) return;
          label.classList.toggle('is-unavailable', !available);
          const hint = label.querySelector('[data-unavailable-label]');
          if (hint) hint.hidden = available;
        });
        const legend = fieldset.querySelector('[data-selected-value]');
        const checked = fieldset.querySelector('input:checked');
        if (legend && checked) legend.textContent = checked.value;
      });
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      this.updateAvailability(selected);
      const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
      this.currentVariant = variant;

      if (!variant) {
        this.setButtons(false, 'Indisponível');
        this.toggleNotify(false);
        return;
      }

      const idInput = this.form && this.form.querySelector('[data-variant-id-input]');
      if (idInput) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (this.dataset.updateUrl !== 'false') {
        window.history.replaceState({}, '', `${this.dataset.productUrl}?variant=${variant.id}`);
      }

      this.querySelectorAll('[data-product-price]').forEach((el) => {
        el.innerHTML = priceMarkup(variant, el.dataset);
      });

      const skuWrap = this.querySelector('[data-sku-wrapper]');
      if (skuWrap) {
        skuWrap.hidden = !variant.sku;
        const sku = skuWrap.querySelector('[data-sku]');
        if (sku) sku.textContent = variant.sku || '';
      }
      const availability = this.querySelector('[data-availability]');
      if (availability) {
        availability.dataset.available = String(variant.available);
        availability.textContent = variant.available ? 'Disponível para envio' : 'Esgotado no momento';
      }

      this.setButtons(variant.available, variant.available ? null : 'Esgotado');
      this.toggleNotify(!variant.available, variant);

      const dynamic = this.querySelector('[data-dynamic-checkout]');
      if (dynamic) dynamic.hidden = !variant.available;

      if (variant.featured_media) {
        this.querySelector('media-gallery')?.showMedia(variant.featured_media.id);
      }

      this.querySelectorAll('shipping-estimator').forEach((el) => {
        el.dataset.variantId = variant.id;
      });

      document.dispatchEvent(
        new CustomEvent('aumi:variant-change', { detail: { variant, sectionId: this.dataset.sectionId } })
      );
    }

    setButtons(enabled, label) {
      this.querySelectorAll('[data-add-button]').forEach((button) => {
        button.disabled = !enabled;
        const text = button.querySelector('[data-add-text]');
        if (text) text.textContent = label || button.dataset.label || 'Adicionar à sacola';
      });
    }

    toggleNotify(show, variant) {
      this.querySelectorAll('[data-notify]').forEach((wrap) => {
        if (wrap.querySelector('[data-notify-success]')) return;
        wrap.hidden = !show;
        const body = wrap.querySelector('[data-notify-body]');
        if (body && variant) {
          body.value = body.value.replace(/\/ .*$/, `/ ${variant.title} (variante ${variant.id})`);
        }
      });
    }
  }

  /* ---------- galeria ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slides]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));

      let scrollTimer;
      this.track.addEventListener(
        'scroll',
        () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => this.syncFromScroll(), 80);
        },
        { passive: true }
      );

      this.addEventListener('click', (event) => {
        const go = event.target.closest('[data-gallery-go]');
        if (go) return this.goTo(Number(go.dataset.galleryGo));
        if (event.target.closest('[data-gallery-prev]')) return this.goTo(this.index - 1);
        if (event.target.closest('[data-gallery-next]')) return this.goTo(this.index + 1);
        const zoom = event.target.closest('[data-open-lightbox]');
        if (zoom) {
          const lightbox = this.closest('product-info')?.querySelector('media-lightbox');
          if (lightbox) lightbox.open(Number(zoom.dataset.openLightbox), zoom);
        }
      });

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        }
      });

      if (this.index > 0) this.goTo(this.index, false);
      else this.setActive(0);

      if (this.hasAttribute('data-has-model')) this.loadModelViewer();
    }

    isGrid() {
      return this.dataset.layout === 'grid' && window.matchMedia('(min-width: 990px)').matches;
    }

    goTo(i, smooth = true) {
      if (!this.slides.length) return;
      const index = Math.min(Math.max(i, 0), this.slides.length - 1);
      const slide = this.slides[index];
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = smooth && !reduce ? 'smooth' : 'auto';
      if (this.isGrid()) {
        const top = slide.getBoundingClientRect().top + window.scrollY - 120;
        if (smooth) window.scrollTo({ top, behavior });
      } else {
        this.track.scrollTo({ left: slide.offsetLeft, behavior });
      }
      this.setActive(index);
    }

    setActive(index) {
      this.index = index;
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        if (!active) {
          slide.querySelectorAll('video').forEach((video) => video.pause());
          slide.querySelectorAll('iframe').forEach((frame) => {
            try {
              frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
              frame.contentWindow.postMessage('{"method":"pause"}', '*');
            } catch (e) {
              /* sem acesso ao player */
            }
          });
        }
      });
      this.querySelectorAll('[data-gallery-go]').forEach((button) => {
        if (Number(button.dataset.galleryGo) === index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === this.slides.length - 1;

      const thumb = this.querySelector(`.gallery__thumb[data-gallery-go="${index}"]`);
      const thumbs = thumb && thumb.closest('.gallery__thumbs');
      if (thumbs && thumbs.scrollHeight > thumbs.clientHeight) {
        const top = thumb.offsetTop - thumbs.clientHeight / 2 + thumb.clientHeight / 2;
        thumbs.scrollTo({ top, behavior: 'smooth' });
      }
    }

    syncFromScroll() {
      if (this.isGrid() || !this.track.clientWidth) return;
      const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (i !== this.index) this.setActive(Math.min(Math.max(i, 0), this.slides.length - 1));
    }

    showMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index < 0) return;
      this.slides.forEach((slide, i) => slide.classList.toggle('is-featured', i === index));
      if (this.isGrid()) {
        this.setActive(index);
        return;
      }
      this.goTo(index);
    }

    loadModelViewer() {
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: (error) => {
            if (error || !window.Shopify.ModelViewerUI) return;
            this.querySelectorAll('model-viewer').forEach((viewer) => new window.Shopify.ModelViewerUI(viewer));
          },
        },
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: (error) => {
            if (error || !window.ShopifyXR) return;
            this.querySelectorAll('[data-shopify-xr]').forEach((button) => {
              button.hidden = false;
            });
          },
        },
      ]);
    }
  }

  /* ---------- lightbox acessível (dialog nativo) ---------- */
  class MediaLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-lightbox]');
      this.track = this.querySelector('[data-lightbox-track]');
      if (!this.dialog || !this.track) return;
      this.slides = Array.from(this.track.children);
      this.index = 0;

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.closest('[data-lightbox-close]')) return this.close();
        if (event.target.closest('[data-lightbox-prev]')) return this.go(this.index - 1);
        if (event.target.closest('[data-lightbox-next]')) return this.go(this.index + 1);
        const zoom = event.target.closest('[data-lightbox-zoom]');
        if (zoom) this.toggleZoom(zoom, event);
      });
      this.dialog.addEventListener('pointermove', (event) => {
        const zoom = event.target.closest('[data-lightbox-zoom].is-zoomed');
        if (zoom) this.pan(zoom, event);
      });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.go(this.index + 1);
        if (event.key === 'ArrowLeft') this.go(this.index - 1);
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('no-scroll');
        this.querySelectorAll('.is-zoomed').forEach((el) => el.classList.remove('is-zoomed'));
        if (this.opener) this.opener.focus({ preventScroll: true });
      });

      let timer;
      this.track.addEventListener(
        'scroll',
        () => {
          clearTimeout(timer);
          timer = setTimeout(() => {
            const i = Math.round(this.track.scrollLeft / (this.track.clientWidth || 1));
            this.setIndex(i);
          }, 80);
        },
        { passive: true }
      );
    }

    open(index, opener) {
      this.opener = opener;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      document.documentElement.classList.add('no-scroll');
      requestAnimationFrame(() => this.go(index, false));
      this.querySelector('[data-lightbox-close]')?.focus();
    }

    close() {
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.removeAttribute('open');
    }

    go(i, smooth = true) {
      const index = Math.min(Math.max(i, 0), this.slides.length - 1);
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      this.setIndex(index);
    }

    setIndex(index) {
      if (index !== this.index) {
        this.querySelectorAll('.is-zoomed').forEach((el) => el.classList.remove('is-zoomed'));
      }
      this.index = index;
      const current = this.querySelector('[data-lightbox-current]');
      if (current) current.textContent = String(index + 1);
      const prev = this.querySelector('[data-lightbox-prev]');
      const next = this.querySelector('[data-lightbox-next]');
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === this.slides.length - 1;
    }

    toggleZoom(zoom, event) {
      const zoomed = zoom.classList.toggle('is-zoomed');
      if (zoomed) this.pan(zoom, event);
    }

    pan(zoom, event) {
      const rect = zoom.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      zoom.style.setProperty('--zoom-x', `${x}%`);
      zoom.style.setProperty('--zoom-y', `${y}%`);
    }
  }

  /* ---------- "Ver mais" ---------- */
  class ReadMore extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-read-more-content]');
      this.toggle = this.querySelector('[data-read-more-toggle]');
      if (!this.content || !this.toggle) return;
      requestAnimationFrame(() => {
        if (this.content.scrollHeight <= this.content.clientHeight + 8) {
          this.content.classList.remove('is-collapsed');
          return;
        }
        this.toggle.hidden = false;
      });
      this.toggle.addEventListener('click', () => {
        const expanded = this.toggle.getAttribute('aria-expanded') === 'true';
        this.toggle.setAttribute('aria-expanded', String(!expanded));
        this.content.classList.toggle('is-collapsed', expanded);
        const label = this.toggle.querySelector('[data-read-more-label]');
        if (label) label.textContent = expanded ? 'Ver mais' : 'Ver menos';
      });
    }
  }

  /* ---------- barra fixa de compra ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.formId);
      const target = form && (form.querySelector('[data-add-button]') || form);
      if (!target || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(([entry]) => {
        this.setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.observer.observe(target);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.documentElement.classList.remove('has-sticky-atc');
      document.documentElement.style.removeProperty('--sticky-atc-height');
    }

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      this.toggleAttribute('inert', !visible);
      this.setAttribute('aria-hidden', String(!visible));
      /* --dock-height pertence à barra inferior (mobile-dock); aqui usamos uma variável própria */
      document.documentElement.classList.toggle('has-sticky-atc', visible);
      document.documentElement.style.setProperty('--sticky-atc-height', visible ? `${this.offsetHeight}px` : '0px');
    }
  }

  /* ---------- "Complete seu ritual" (recomendações complementares) ---------- */
  class ComplementaryProducts extends HTMLElement {
    connectedCallback() {
      const block = this.closest('.product__block');
      if (!this.dataset.url) {
        if (!this.querySelector('[data-complementary-item]') && block) block.hidden = true;
        return;
      }
      if (block) block.hidden = true;
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.getElementById(this.id);
          if (fresh && fresh.querySelector('[data-complementary-item]')) {
            this.innerHTML = fresh.innerHTML;
            if (block) block.hidden = false;
          }
        })
        .catch(() => {});
    }
  }

  /* ---------- simulador de frete ---------- */
  const UF_BY_CEP = [
    [1000, 19999, 'SP'], [20000, 28999, 'RJ'], [29000, 29999, 'ES'], [30000, 39999, 'MG'],
    [40000, 48999, 'BA'], [49000, 49999, 'SE'], [50000, 56999, 'PE'], [57000, 57999, 'AL'],
    [58000, 58999, 'PB'], [59000, 59999, 'RN'], [60000, 63999, 'CE'], [64000, 64999, 'PI'],
    [65000, 65999, 'MA'], [66000, 68899, 'PA'], [68900, 68999, 'AP'], [69000, 69299, 'AM'],
    [69300, 69399, 'RR'], [69400, 69899, 'AM'], [69900, 69999, 'AC'], [70000, 72799, 'DF'],
    [72800, 72999, 'GO'], [73000, 73699, 'DF'], [73700, 76799, 'GO'], [76800, 76999, 'RO'],
    [77000, 77999, 'TO'], [78000, 78899, 'MT'], [79000, 79999, 'MS'], [80000, 87999, 'PR'],
    [88000, 89999, 'SC'], [90000, 99999, 'RS'],
  ];

  const ufFromCep = (digits) => {
    const prefix = parseInt(digits.slice(0, 5), 10);
    const hit = UF_BY_CEP.find(([min, max]) => prefix >= min && prefix <= max);
    return hit ? hit[2] : '';
  };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  class ShippingEstimator extends HTMLElement {
    connectedCallback() {
      this.formEl = this.querySelector('[data-cep-form]');
      this.input = this.querySelector('[data-cep-input]');
      this.result = this.querySelector('[data-cep-result]');
      this.submit = this.querySelector('[data-cep-submit]');
      if (!this.formEl || !this.input) return;

      const saved = store.get(CEP_KEY, '');
      if (saved) this.input.value = this.mask(saved);

      this.input.addEventListener('input', () => {
        this.input.value = this.mask(this.input.value);
        this.input.removeAttribute('aria-invalid');
      });
      this.formEl.addEventListener('submit', (event) => {
        event.preventDefault();
        this.estimate();
      });
      this.result.addEventListener('click', (event) => {
        if (event.target.closest('[data-cep-add]')) this.addAndEstimate(event.target.closest('[data-cep-add]'));
      });
    }

    mask(value) {
      const digits = String(value).replace(/\D/g, '').slice(0, 8);
      return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    }

    digits() {
      return this.input.value.replace(/\D/g, '');
    }

    setLoading(loading) {
      this.submit.classList.toggle('is-loading', loading);
      this.submit.disabled = loading;
      this.setAttribute('aria-busy', String(loading));
    }

    message(text, tone = '') {
      this.result.innerHTML = `<p class="shipping-estimator__message${tone ? ` shipping-estimator__message--${tone}` : ''}">${text}</p>`;
    }

    async estimate() {
      const zip = this.digits();
      if (zip.length !== 8 || !ufFromCep(zip)) {
        this.input.setAttribute('aria-invalid', 'true');
        this.message('Digite um CEP válido com 8 números.', 'error');
        this.input.focus();
        return;
      }
      store.set(CEP_KEY, zip);
      this.setLoading(true);
      try {
        const cart = await fetch(`${cartBase()}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        if (!cart.item_count) {
          this.result.innerHTML = `
            <p class="shipping-estimator__message">Adicione à sacola para calcular o frete exato.</p>
            <button type="button" class="button button--outline button--sm" data-cep-add>Adicionar à sacola e calcular</button>`;
          return;
        }
        await this.calculate(zip);
      } catch (error) {
        this.message('Não conseguimos calcular agora. Tente de novo em instantes.', 'error');
      } finally {
        this.setLoading(false);
      }
    }

    async addAndEstimate(button) {
      const id = Number(this.dataset.variantId);
      if (!id) return;
      button.classList.add('is-loading');
      try {
        const response = await fetch(`${(routes().cartAdd || '/cart/add').replace(/\/$/, '')}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Não foi possível adicionar.');
        if (aumi().cart && typeof aumi().cart.refresh === 'function') aumi().cart.refresh();
        toast('Adicionado à sacola');
        this.setLoading(true);
        await this.calculate(this.digits());
      } catch (error) {
        this.message(escapeHtml(error.message || 'Não foi possível adicionar.'), 'error');
      } finally {
        this.setLoading(false);
      }
    }

    async calculate(zip) {
      const params = new URLSearchParams();
      params.set('shipping_address[zip]', this.mask(zip));
      params.set('shipping_address[country]', 'BR');
      params.set('shipping_address[province]', ufFromCep(zip));
      const query = params.toString();

      this.message('Calculando as opções de entrega…');
      const prepare = await fetch(`${cartBase()}/prepare_shipping_rates.json?${query}`, { method: 'POST' });
      if (!prepare.ok) return this.renderError(await prepare.json().catch(() => ({})));

      for (let attempt = 0; attempt < 12; attempt += 1) {
        const response = await fetch(`${cartBase()}/async_shipping_rates.json?${query}`);
        if (!response.ok) return this.renderError(await response.json().catch(() => ({})));
        const data = await response.json().catch(() => null);
        if (data && Array.isArray(data.shipping_rates)) return this.renderRates(data.shipping_rates);
        await wait(600 + attempt * 150);
      }
      this.message('O cálculo demorou mais que o esperado. Tente de novo.', 'error');
      return null;
    }

    renderError(error) {
      const first = error && typeof error === 'object' ? Object.values(error).flat()[0] : '';
      const detail = first ? ` <span class="muted">(${escapeHtml(first)})</span>` : '';
      this.message(`Não encontramos entrega para esse CEP. Confira os números.${detail}`, 'error');
    }

    renderRates(rates) {
      if (!rates.length) {
        this.message('Ainda não entregamos nesse CEP. Fale com a gente para encontrar um caminho.', 'error');
        return;
      }
      const items = rates
        .map((rate) => {
          const cents = Math.round(parseFloat(rate.price) * 100);
          const days = Array.isArray(rate.delivery_days) ? rate.delivery_days.filter((d) => d != null) : [];
          let prazo = '';
          if (days.length === 2 && days[0] !== days[1]) prazo = `de ${days[0]} a ${days[1]} dias úteis`;
          else if (days.length) prazo = `até ${days[days.length - 1]} ${Number(days[days.length - 1]) === 1 ? 'dia útil' : 'dias úteis'}`;
          return `<li class="shipping-rates__item">
              <span class="shipping-rates__name">${escapeHtml(rate.presentment_name || rate.name)}${prazo ? `<span class="shipping-rates__eta muted">${prazo}</span>` : ''}</span>
              <span class="shipping-rates__price">${cents === 0 ? 'Grátis' : money(cents)}</span>
            </li>`;
        })
        .join('');
      this.result.innerHTML = `<ul class="shipping-rates" role="list">${items}</ul>
        <p class="shipping-estimator__note muted">Valores para os itens da sua sacola no CEP ${escapeHtml(this.mask(this.digits()))}.</p>`;
    }
  }

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  define('product-info', ProductInfo);
  define('media-gallery', MediaGallery);
  define('media-lightbox', MediaLightbox);
  define('read-more', ReadMore);
  define('sticky-atc', StickyAtc);
  define('complementary-products', ComplementaryProducts);
  define('shipping-estimator', ShippingEstimator);

  /* ---------- copiar (cupom e link) e compartilhar ---------- */
  document.addEventListener('click', async (event) => {
    const copy = event.target.closest('[data-copy]');
    if (copy) {
      const original = copy.dataset.originalLabel || copy.textContent.trim();
      copy.dataset.originalLabel = original;
      try {
        await navigator.clipboard.writeText(copy.dataset.copy);
        copy.textContent = copy.dataset.copyDone || 'Copiado';
        toast(copy.dataset.copyDone || 'Copiado');
      } catch (e) {
        copy.textContent = copy.dataset.copy;
      }
      setTimeout(() => {
        copy.textContent = original;
      }, 2400);
      return;
    }
    const share = event.target.closest('[data-share-native]');
    if (share && navigator.share) {
      navigator.share({ title: share.dataset.shareTitle, url: share.dataset.shareUrl }).catch(() => {});
    }
  });

  const revealShare = () => {
    if (!navigator.share) return;
    document.querySelectorAll('[data-share-native]').forEach((button) => {
      button.hidden = false;
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', revealShare);
  else revealShare();

  /* editor de temas: re-inicializa ao recarregar a seção */
  document.addEventListener('shopify:section:load', revealShare);
})();
